// Sweet Match - minimal position-based physics. No DOM, no game rules.
//
// Deliberately narrow scope: circles against static line segments. No rotation,
// no restitution, no joints, no continuous collision. That is exactly the
// subset this game needs, which is why there is no physics library in the
// project (see docs/PLAN-nieuw-spel-sweetmatch.md §3).
//
// Units are candy diameters (1 unit = one candy wide), y positive downward.
// index.ts scales units -> px; the simulation never sees pixels. Combined with
// the fixed timestep below that makes a headless run reproduce the on-screen
// result exactly, which is what the winnability bot in the validator relies on.
//
// Integration is Verlet: velocity is *derived* from the previous position
// (v = x - px) instead of stored. Pushing a body out of a wall therefore also
// removes the velocity that drove it in - collisions are inelastic for free and
// stacks settle quietly without an impulse solver.

/** Fixed physics step. Never integrate with a raw frame delta. */
export const STEP_MS = 1000 / 120;
/** Max steps per frame, so a long stall cannot spiral into a freeze. */
export const MAX_CATCHUP = 5;
/** Frame deltas above this are treated as a stall, not as elapsed time. */
export const MAX_FRAME_MS = 50;

/** Relaxation passes per step. 4 is enough for the stack depths in this game. */
export const ITERS = 4;
/** A body moving less than this per step counts as standing still. */
export const SETTLE_EPS = 0.002;

/**
 * The feel knobs, and the only mutable state in this module.
 *
 * - `gravity` in units/s²: how heavy the candy fall reads.
 * - `damp`: per-step velocity retention (air drag); also what finally kills a
 *   rattle so bodies can register as settled.
 * - `friction`: tangential damping per step of sustained contact. Low on
 *   purpose - candies must still slide down the funnel edges. At 0.02 a slope
 *   settles at roughly 7 units/s of sliding versus ~26 units/s in free fall:
 *   "rustig glijden", not "vastgeplakt".
 *
 * Mutable so probe-sweetmatch.html can tune these live. **The game itself never
 * writes to this** - changing values mid-run would break the determinism the
 * headless validator depends on.
 */
export const tuning = { gravity: 26, damp: 0.995, friction: 0.02 };

/** The shipped values, for the probe's reset button and for tests. */
export const DEFAULT_TUNING: Readonly<typeof tuning> = { ...tuning };

const H = STEP_MS / 1000;

export interface Body {
  id: number;
  /** Center in units. */
  x: number;
  y: number;
  /** Previous center - the velocity carrier (v = x - px). */
  px: number;
  py: number;
  r: number;
  /** false = frozen in mid-air (an untapped candy); still collides. */
  dynamic: boolean;
  /** How long this body has been standing still, in ms. */
  settledMs: number;
}

/** All static geometry is a line segment: walls, funnel edges, neck, floor. */
export interface Segment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

export interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface World {
  bodies: Body[];
  segments: Segment[];
  bounds: Bounds;
  /** Leftover time that did not fill a whole step yet. */
  accMs: number;
}

export function makeBody(id: number, x: number, y: number, r: number, dynamic = false): Body {
  return { id, x, y, px: x, py: y, r, dynamic, settledMs: 0 };
}

export function createWorld(segments: Segment[], bounds: Bounds): World {
  return { bodies: [], segments, bounds, accMs: 0 };
}

export function removeBodies(world: World, ids: ReadonlySet<number>): void {
  world.bodies = world.bodies.filter((b) => !ids.has(b.id));
}

/** Current velocity in units per step (handy for tests and the fail check). */
export function speed(b: Body): number {
  return Math.hypot(b.x - b.px, b.y - b.py);
}

/**
 * Closest point on a segment to (x, y), plus its distance. Used both by the
 * collision pass and by level generation, which needs the same notion of "how
 * far is this spot from a wall" to avoid spawning candies inside the funnel.
 */
export function closestOnSegment(
  x: number,
  y: number,
  s: Segment,
): { cx: number; cy: number; dist: number } {
  const ex = s.bx - s.ax;
  const ey = s.by - s.ay;
  const len2 = ex * ex + ey * ey;
  let t = len2 > 0 ? ((x - s.ax) * ex + (y - s.ay) * ey) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = s.ax + ex * t;
  const cy = s.ay + ey * t;
  return { cx, cy, dist: Math.hypot(x - cx, y - cy) };
}

/**
 * Run whole fixed steps for the elapsed time. Returns how many steps ran, so
 * the caller can skip work on a frame that produced none.
 */
export function advance(world: World, dtMs: number): number {
  world.accMs += Math.min(dtMs, MAX_FRAME_MS);
  let steps = 0;
  while (world.accMs >= STEP_MS && steps < MAX_CATCHUP) {
    world.accMs -= STEP_MS;
    step(world);
    steps++;
  }
  // Drop the remainder if we hit the catch-up ceiling, so a slow device does
  // not build up a debt it can never repay.
  if (world.accMs > STEP_MS * MAX_CATCHUP) world.accMs = 0;
  return steps;
}

/** Friction share per relaxation pass; refreshed from `tuning` each step. */
let frictionIter = tuning.friction / ITERS;

/** One fixed step. Exported for tests and the headless bot. */
export function step(world: World): void {
  const { bodies, segments, bounds } = world;
  const damp = tuning.damp;
  const gravStep = tuning.gravity * H * H; // units per step²
  frictionIter = tuning.friction / ITERS;

  for (const b of bodies) {
    if (!b.dynamic) continue;
    let vx = (b.x - b.px) * damp;
    let vy = (b.y - b.py) * damp + gravStep;
    // Cap the per-step move well below the radius. If a body could cross a
    // wall's centerline in one step, the closest-point normal would flip and
    // the next relaxation pass would push it out on the wrong side - straight
    // through the wall. 0.6r leaves plenty of margin and sits above the speeds
    // reached by falling the height of the funnel, so it never binds in play.
    const max = 0.6 * b.r;
    const sp = Math.hypot(vx, vy);
    if (sp > max) {
      const k = max / sp;
      vx *= k;
      vy *= k;
    }
    b.px = b.x;
    b.py = b.y;
    b.x += vx;
    b.y += vy;
  }

  for (let i = 0; i < ITERS; i++) {
    resolvePairs(bodies);
    resolveSegments(bodies, segments);
  }

  for (const b of bodies) {
    if (!b.dynamic) continue;
    clampBounds(b, bounds);
    b.settledMs = speed(b) < SETTLE_EPS ? b.settledMs + STEP_MS : 0;
  }
}

/**
 * Remove the fraction of tangential velocity that friction would eat. Works by
 * shifting the previous position, because that is where velocity lives.
 */
function rubTangent(b: Body, nx: number, ny: number): void {
  const vt = (b.x - b.px) * -ny + (b.y - b.py) * nx;
  b.px -= ny * frictionIter * vt;
  b.py += nx * frictionIter * vt;
}

/** O(n²) over pairs with at least one dynamic body - fine up to ~64 candies. */
function resolvePairs(bodies: Body[]): void {
  for (let i = 0; i < bodies.length; i++) {
    const a = bodies[i];
    for (let j = i + 1; j < bodies.length; j++) {
      const b = bodies[j];
      if (!a.dynamic && !b.dynamic) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const min = a.r + b.r;
      let dist = Math.hypot(dx, dy);
      if (dist >= min) continue;
      let nx: number;
      let ny: number;
      if (dist < 1e-9) {
        // Exactly coincident: pick a fixed axis so the outcome stays deterministic.
        nx = 0;
        ny = 1;
        dist = 0;
      } else {
        nx = dx / dist;
        ny = dy / dist;
      }
      const overlap = min - dist;
      if (a.dynamic && b.dynamic) {
        const half = overlap * 0.5;
        a.x -= nx * half;
        a.y -= ny * half;
        b.x += nx * half;
        b.y += ny * half;
        rubTangent(a, nx, ny);
        rubTangent(b, nx, ny);
      } else if (a.dynamic) {
        a.x -= nx * overlap;
        a.y -= ny * overlap;
        rubTangent(a, nx, ny);
      } else {
        b.x += nx * overlap;
        b.y += ny * overlap;
        rubTangent(b, nx, ny);
      }
    }
  }
}

function resolveSegments(bodies: Body[], segments: Segment[]): void {
  for (const b of bodies) {
    if (!b.dynamic) continue;
    for (const s of segments) {
      const near = closestOnSegment(b.x, b.y, s);
      let dist = near.dist;
      if (dist >= b.r) continue;
      const dx = b.x - near.cx;
      const dy = b.y - near.cy;
      let nx: number;
      let ny: number;
      if (dist < 1e-9) {
        // Center exactly on the line: push out along the segment's normal.
        const ex = s.bx - s.ax;
        const ey = s.by - s.ay;
        const len = Math.hypot(ex, ey) || 1;
        nx = -ey / len;
        ny = ex / len;
        dist = 0;
      } else {
        nx = dx / dist;
        ny = dy / dist;
      }
      const push = b.r - dist;
      b.x += nx * push;
      b.y += ny * push;
      rubTangent(b, nx, ny);
    }
  }
}

/** Last-resort containment; the walls do the real work. */
function clampBounds(b: Body, q: Bounds): void {
  if (b.x < q.x0 + b.r) b.x = q.x0 + b.r;
  else if (b.x > q.x1 - b.r) b.x = q.x1 - b.r;
  if (b.y > q.y1 - b.r) b.y = q.y1 - b.r;
  // No ceiling clamp: nothing in this game ever travels upward out of the field.
}
